import useBottomSheet from 'hooks/common/useBottomSheet';
import { sendClickLog, type ClientContent } from 'pages/notification/notificationService';
import { useCallback } from 'react';
import type { Notification } from 'types/notification';

const COMMUNITY_RULES_URL = 'https://www.thebermuda.net/communityguide-en-us.html';

/** 럭키박스 알림이 가리키는 구독 상품. */
const LUCKYBOX_PRODUCT = 'csubs_12000';

const useNotificationAction = () => {
  const { open: openSheet } = useBottomSheet();

  /** 상점은 알림 화면을 먼저 닫고 연다 — 그러지 않으면 네비게이션이 꼬인다. */
  const goShop = useCallback(() => {
    Transition.back();
    Transition.showPage(Def.PAGE_SHOP);
    sendClickLog('navigate_to_other_page', { nextPage: Def.PAGE_SHOP });
  }, []);

  const goInquiry = useCallback(() => {
    Transition.showPage(Def.PAGE_INQUIRY);
    sendClickLog('navigate_to_other_page', { nextPage: Def.PAGE_INQUIRY });
  }, []);

  const goCommunityRules = useCallback(() => {
    Transition.showPage(Def.PAGE_IFRAME, { title: 'Community Rules', url: COMMUNITY_RULES_URL });
    sendClickLog('navigate_to_other_page', { nextPage: Def.PAGE_IFRAME });
  }, []);

  const openProfile = useCallback(
    async (item: Notification) => {
      const fbUid = item.sender?.fbUid;
      if (!fbUid || fbUid === myinfo.fbUid) return;

      const isFriend = await FriendsDataManager.isFriendAsync(fbUid);
      openSheet('Profile', { data: { fbUid, category: 'Notification', type: isFriend ? 'friend' : 'message' } });
    },
    [openSheet]
  );

  const call = useCallback((item: Notification) => {
    const sender = item.sender;
    if (!sender) return;

    CommonUI.makeCall(sender, () => {}, item.category, { fromWhere: 'notification' });
    sendClickLog('missed_call', { actionFbUid: sender.fbUid, actionGender: sender.gender });
  }, []);

  const chat = useCallback((item: Notification) => {
    const sender = item.sender;
    if (!sender) return;

    // 로그의 btn 값은 옛 이름 그대로 둔다 — 바꾸면 기존 집계가 끊긴다.
    CommonUI.tryChat(sender.fbUid, sender.nick, sender.gender, sender.country, () => {}, false, true, false, 'noti', {
      page: Transition.getCurrentPage() || Transition.getCurrentTab(),
      btn: 'cafe-card-message',
      prevLoc: Transition.getPrevHistoryName(),
    });
    sendClickLog('navigate_to_other_page', { nextPage: 'TryChat' });
  }, []);

  const openLuckybox = useCallback(() => {
    PageShop.handleProductAction(LUCKYBOX_PRODUCT);
    sendClickLog('navigate_to_other_page', { nextPage: 'PageMembership' });
  }, []);

  const openTimeBuff = useCallback(() => {
    if (ItemDataManager.getItemCount('timeBuff')) {
      openSheet('TimeBuffFilter');
      return;
    }
    // 상점을 열지 않으면 네비게이션이 꼬인다.
    Transition.back();
    Transition.showPage(Def.PAGE_SHOP);
    openSheet('TimeBuffPurchase');
  }, [openSheet]);

  /** system/client 알림의 버튼. 지금 있는 값은 두 개뿐이다. */
  const followClientLink = useCallback((link: ClientContent['link']) => {
    if (link === 'AppReview') {
      Device.openRateUs();
      sendClickLog('app_review', {});
      return;
    }
    if (link === 'TabHome') {
      Transition.clearNmoveTab(Def.TAB_HOME);
      sendClickLog('navigate_to_other_page', { nextPage: Def.TAB_HOME });
    }
  }, []);

  return { openProfile, call, chat, goShop, goInquiry, goCommunityRules, openLuckybox, openTimeBuff, followClientLink };
};

export default useNotificationAction;
